import React, { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Alert, Button, DatePicker, Form, Input, Result, Select, Space, Spin, Tag, message } from 'antd'

import Layout from '../App/layout'
import LayoutContentWrapper from '../../components/utility/layoutWrapper'
import ContentLayout from '../../components/utility/layoutContent'

import {
  CREATE_LEAVE_DAY_MUTATION,
  LEAVE_DAY_QUERY,
  LEAVE_STATUSES,
  LEAVE_TYPES,
  STATUS_COLORS,
  UPDATE_LEAVE_DAY_MUTATION,
  formatMutationErrors
} from './graphql'

const DATE_FORMAT = 'YYYY-MM-DD'
const DISPLAY_FORMAT = 'DD/MM/YYYY'

const toFormValues = leaveDay => ({
  dates: leaveDay.startDate && leaveDay.endDate
    ? [dayjs(leaveDay.startDate), dayjs(leaveDay.endDate)]
    : null,
  leaveType: leaveDay.leaveType || undefined,
  description: leaveDay.description || '',
  status: leaveDay.status || undefined
})

const LeaveDayFormContainer = () => {
  const { t } = useTranslation('leaveDays')
  const history = useHistory()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  const { data, loading } = useQuery(LEAVE_DAY_QUERY, {
    skip: !isEdit,
    variables: { id },
    fetchPolicy: 'network-only'
  })

  const [createLeaveDay] = useMutation(CREATE_LEAVE_DAY_MUTATION)
  const [updateLeaveDay] = useMutation(UPDATE_LEAVE_DAY_MUTATION)

  const leaveDay = data && data.leaveDay
  const editable = !isEdit || Boolean(leaveDay && leaveDay.canUpdate)
  const canApprove = Boolean(leaveDay && leaveDay.canApprove)
  const pageTitle = isEdit ? t('editLeave') : t('newLeave')

  useEffect(() => {
    document.title = 'Leave Days | OSH CRM'
  }, [])

  useEffect(() => {
    if (leaveDay) form.setFieldsValue(toFormValues(leaveDay))
  }, [leaveDay])

  const buildVariables = ({ dates, leaveType, description, status }) => {
    const variables = {
      startDate: dates[0].format(DATE_FORMAT),
      endDate: dates[1].format(DATE_FORMAT),
      leaveType,
      description: description || null
    }

    if (!isEdit) return variables
    if (canApprove && status) return { ...variables, id, status }

    return { ...variables, id }
  }

  const handleSubmit = async (values) => {
    setSaving(true)
    try {
      const mutate = isEdit ? updateLeaveDay : createLeaveDay
      const { data: response } = await mutate({ variables: buildVariables(values) })
      const payload = response && (isEdit ? response.updateLeaveDay : response.createLeaveDay)
      if (!payload) { message.error(t('saveFailed')); return }
      if (payload.errors && payload.errors.length) { message.error(formatMutationErrors(payload.errors)); return }

      message.success(t('saveSuccess'))
      history.push('/leave_days')
    } catch (error) {
      message.error(error.message)
    } finally {
      setSaving(false)
    }
  }

  const renderBody = () => {
    if (isEdit && loading) return <Spin />

    if (isEdit && !leaveDay) {
      return (
        <Result
          extra={<Link to='/leave_days'>{t('backToList')}</Link>}
          status='404'
          title={t('notFound')}
        />
      )
    }

    return (
      <>
        {isEdit && leaveDay.staff && (
          <p>
            {t('staff')}: <strong>{leaveDay.staff.fullName}</strong>
            {leaveDay.status && (
              <Tag color={STATUS_COLORS[leaveDay.status]} style={{ marginLeft: 8 }}>
                {t(`statuses.${leaveDay.status}`)}
              </Tag>
            )}
          </p>
        )}

        {!editable && (
          <Alert message={t('lockedApproved')} showIcon style={{ marginBottom: 16 }} type='info' />
        )}

        <Form
          disabled={!editable || saving}
          form={form}
          initialValues={{ description: '' }}
          layout='vertical'
          onFinish={handleSubmit}
          style={{ maxWidth: 560 }}
        >
          <Form.Item
            label={t('dates')}
            name='dates'
            rules={[{ required: true, message: t('required') }]}
          >
            <DatePicker.RangePicker format={DISPLAY_FORMAT} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label={t('leaveType')}
            name='leaveType'
            rules={[{ required: true, message: t('required') }]}
          >
            <Select placeholder={t('leaveType')}>
              {LEAVE_TYPES.map(value => (
                <Select.Option key={value} value={value}>{t(`leaveTypes.${value}`)}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label={t('description')} name='description'>
            <Input.TextArea rows={4} />
          </Form.Item>

          {isEdit && canApprove && (
            <Form.Item label={t('status')} name='status'>
              <Select>
                {LEAVE_STATUSES.map(value => (
                  <Select.Option key={value} value={value}>{t(`statuses.${value}`)}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Space>
            {editable && (
              <Button htmlType='submit' loading={saving} type='primary'>
                {isEdit ? t('save') : t('submitLeave')}
              </Button>
            )}
            <Link to='/leave_days'>{t('backToList')}</Link>
          </Space>
        </Form>
      </>
    )
  }

  return (
    <Layout>
      <LayoutContentWrapper breadcrumbs={[
        { path: '/leave_days', name: t('title') },
        { name: pageTitle }
      ]}>
        <ContentLayout>
          <h2>{pageTitle}</h2>
          {renderBody()}
        </ContentLayout>
      </LayoutContentWrapper>
    </Layout>
  )
}

LeaveDayFormContainer.propTypes = {}

LeaveDayFormContainer.defaultProps = {}

export default LeaveDayFormContainer
